import { createSlice } from '@reduxjs/toolkit'
import Etage from 'interfaces/Etage'
import { LOCATION_CHANGE } from 'connected-react-router'
import Location from 'interfaces/Location'
import { calculAllSurfaces } from 'utils/calculSurfaces'
import { TypeBien } from 'interfaces/API'
import DisplayMode from '../../../interfaces/DisplayMode'

export interface BienAVendreState {
  locations: Location[]
  typeBien: TypeBien | null
  surfaceTerrainMin: number
  surfaceTerrainMax: number
  surfaceHabitable: number | null
  surfaceHabitableMin: number | null
  surfaceHabitableMax: number | null
  surfaceAuSolMin: number | null
  surfaceAuSolMax: number | null
  piscine: boolean | null
  batiMitoyen: boolean | null
  etages: Etage[]
  showedCalculator: boolean
  showedRayonForm: boolean
  calculViaConfigurateurAvance: boolean
  garage: number | null
  garagePosition: number | null
  labelGarage: number | null
  garageSpace: number
  doNotUseSurfaceHabitable: boolean
  particularShapeOrGarage: number | null
  displayMode: DisplayMode
}

// Valeurs par défaut, utilisées à l'init et au reset
export const initialState: BienAVendreState = {
  locations: [],
  typeBien: TypeBien.Maison,
  surfaceTerrainMin: 0,
  surfaceTerrainMax: 6000,
  surfaceHabitable: null,
  surfaceHabitableMin: null,
  surfaceHabitableMax: null,
  surfaceAuSolMin: null,
  surfaceAuSolMax: null,
  piscine: null,
  batiMitoyen: null,
  etages: [],
  showedCalculator: false,
  showedRayonForm: false,
  calculViaConfigurateurAvance: false,
  garage: null,
  garagePosition: null,
  labelGarage: null,
  garageSpace: 0,
  doNotUseSurfaceHabitable: false,
  particularShapeOrGarage: null,
  displayMode: { type: 'normal' },
}

const name = 'bienAVendre'

const bienAVendreSlice = createSlice({
  name,
  initialState,
  reducers: {
    // Redux Toolkit allows us to write "mutating" logic in reducers. It
    // doesn't actually mutate the state because it uses the Immer library,
    // which detects changes to a "draft state" and produces a brand new
    // immutable state based off those changes
    [LOCATION_CHANGE]: (state) => {
      state.displayMode = initialState.displayMode
    },
    changeTypeBien: (state, action) => {
      const typeBien = action.payload

      state.typeBien = typeBien
    },
    updateLocations: (state, action) => {
      const locations = action.payload

      state.locations = locations
    },
    changeSurfaceTerrainMin: (state, action) => {
      const surfaceTerrainMin = action.payload

      state.surfaceTerrainMin = surfaceTerrainMin
    },
    changeSurfaceTerrainMax: (state, action) => {
      const surfaceTerrainMax = action.payload

      state.surfaceTerrainMax = surfaceTerrainMax
    },
    changeSurfaceHabitable: (state, action) => {
      const surfaceHabitable = action.payload

      return {
        ...state,
        surfaceHabitable,
        ...calculAllSurfaces({
          surfaceInput: surfaceHabitable,
          calculViaConfigurateurAvance: state.calculViaConfigurateurAvance,
          garagePosition: state.garagePosition,
          tailleGarage: state.garageSpace,
          etages: state.etages,
        }),
      }
    },
    changeSurfaceAuSolMin: (state, action) => {
      const surfaceAuSolMin = action.payload
      const surfaceAuSolMax =
        state.surfaceAuSolMax === null ||
        state.surfaceAuSolMax < surfaceAuSolMin
          ? surfaceAuSolMin
          : state.surfaceAuSolMax
      return {
        ...state,
        surfaceAuSolMin,
        surfaceAuSolMax,
      }
    },
    changeSurfaceAuSolMax: (state, action) => {
      const surfaceAuSolMax = action.payload
      const surfaceAuSolMin =
        state.surfaceAuSolMin === null ? 0 : state.surfaceAuSolMin
      return {
        ...state,
        surfaceAuSolMin,
        surfaceAuSolMax,
      }
    },
    updateEtages: (state, action) => {
      const etages = action.payload

      let newState = {
        ...state,
        etages,
      }
      if (state.surfaceHabitable !== null) {
        newState = {
          ...newState,
          ...calculAllSurfaces({
            surfaceInput: state.surfaceHabitable,
            calculViaConfigurateurAvance: state.calculViaConfigurateurAvance,
            garagePosition: state.garagePosition,
            tailleGarage: state.garageSpace,
            etages,
          }),
        }
      }
      return newState
    },
    addEtages: (state) => {
      const newetage = {
        width: state.etages.length > 0 ? state.etages[0].width : 100,
        position: {
          x: state.etages.length > 0 ? state.etages[0].position.x : 0,
          y: 0,
        },
      }

      const etages = [newetage, ...state.etages]
      let newState = {
        ...state,
        etages,
      }
      if (state.surfaceHabitable !== null) {
        newState = {
          ...newState,
          ...calculAllSurfaces({
            surfaceInput: state.surfaceHabitable,
            calculViaConfigurateurAvance: state.calculViaConfigurateurAvance,
            garagePosition: state.garagePosition,
            tailleGarage: state.garageSpace,
            etages,
          }),
        }
      }
      return newState
    },
    deleteEtages: (state) => {
      const etages = state.etages.slice(1)
      let newState = {
        ...state,
        etages,
      }
      if (state.surfaceHabitable !== null) {
        newState = {
          ...newState,
          ...calculAllSurfaces({
            surfaceInput: state.surfaceHabitable,
            calculViaConfigurateurAvance: state.calculViaConfigurateurAvance,
            garagePosition: state.garagePosition,
            tailleGarage: state.garageSpace,
            etages,
          }),
        }
      }
      return newState
    },
    resetFormulaire: (state) => ({
      ...state,
      ...initialState,
    }),
    changePiscine: (state, action) => {
      const piscine = action.payload

      return {
        ...state,
        piscine,
      }
    },
    changeBatiMitoyen: (state, action) => {
      const batiMitoyen = action.payload

      return {
        ...state,
        batiMitoyen,
      }
    },
    showCalculator: (state, action) => {
      const showedCalculator = action.payload

      return {
        ...state,
        showedCalculator,
      }
    },
    showRayonForm: (state, action) => {
      const showedRayonForm = action.payload

      return {
        ...state,
        showedRayonForm,
      }
    },
    resetGarage: (state) => ({
      ...state,
      garage: initialState.garage,
      garagePosition: initialState.garagePosition,
      labelGarage: initialState.labelGarage,
      garageSpace: initialState.garageSpace,
    }),
    initGarage: (state, action) => {
      const garage = action.payload

      return {
        ...state,
        garage,
      }
    },
    initLabelGarage: (state, action) => {
      const labelGarage = action.payload

      return {
        ...state,
        labelGarage,
      }
    },
    initGarageSpace: (state, action) => {
      const garageSpace = action.payload

      let newState = {
        ...state,
        garageSpace,
      }
      if (state.surfaceHabitable !== null) {
        newState = {
          ...newState,
          ...calculAllSurfaces({
            surfaceInput: state.surfaceHabitable,
            calculViaConfigurateurAvance: state.calculViaConfigurateurAvance,
            garagePosition: state.garagePosition,
            tailleGarage: garageSpace,
            etages: state.etages,
          }),
        }
      }
      return newState
    },
    initGaragePosition: (state, action) => {
      const garagePosition = action.payload

      return {
        ...state,
        garagePosition,
      }
    },
    setCalculViaConfigurateurAvance: (state, action) => {
      let newState = {
        ...state,
        calculViaConfigurateurAvance: action.payload,
      }
      if (state.surfaceHabitable !== null) {
        newState = {
          ...newState,
          ...calculAllSurfaces({
            surfaceInput: state.surfaceHabitable,
            calculViaConfigurateurAvance: action.payload,
            garagePosition: state.garagePosition,
            tailleGarage: state.garageSpace,
            etages: state.etages,
          }),
        }
      }
      return newState
    },
    toggleDoNotUseSurfaceHabitable: (state) => ({
      ...state,
      doNotUseSurfaceHabitable: !state.doNotUseSurfaceHabitable,
    }),
    updateParticularShapeOrGarage: (state, action) => {
      const particularShapeOrGarage = action.payload

      return {
        ...state,
        particularShapeOrGarage,
      }
    },
    toggleDisplayMode: (state, action) => {
      const displayMode = action.payload

      if (displayMode)
        return {
          ...state,
          displayMode,
        }

      return {
        ...state,
        displayMode: {
          type: state.displayMode.type === 'normal' ? 'synthese' : 'normal',
        },
      }
    },
  },
})

export const {
  changeTypeBien,
  updateLocations,
  changeSurfaceTerrainMin,
  changeSurfaceTerrainMax,
  changeSurfaceHabitable,
  changeSurfaceAuSolMin,
  changeSurfaceAuSolMax,
  updateEtages,
  addEtages,
  deleteEtages,
  resetFormulaire,
  changePiscine,
  changeBatiMitoyen,
  showCalculator,
  resetGarage,
  initGarage,
  initLabelGarage,
  initGarageSpace,
  initGaragePosition,
  setCalculViaConfigurateurAvance,
  toggleDoNotUseSurfaceHabitable,
  updateParticularShapeOrGarage,
  toggleDisplayMode,
} = bienAVendreSlice.actions

export default bienAVendreSlice
